import { useAdminReviews, type AdminReview, type AdminReviewSort } from '@fidalia/api';
import {
  Alert,
  DataTable,
  Input,
  PageHeader,
  Pagination,
  Select,
  ViewSwitch,
  type ReviewsView,
} from '@fidalia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { MdBusiness, MdRateReview, MdSearch, MdSort, MdStar } from 'react-icons/md';

import { AvisGrille, AvisGrilleSqueletes } from '@/features/reviews/avis-grille';
import { AvisModale } from '@/features/reviews/avis-modale';
import { colonnesAvis } from '@/features/reviews/colonnes';
import { errorMessage } from '@/lib/auth';
import { useDebounced } from '@/lib/use-debounced';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_app/reviews')({
  // `?cabinetId=` : arrivée depuis la liste des formulaires, déjà filtrée sur un cabinet.
  validateSearch: (search: Record<string, unknown>): { cabinetId?: string } => ({
    cabinetId:
      typeof search.cabinetId === 'string' && search.cabinetId ? search.cabinetId : undefined,
  }),
  component: AvisPage,
});

const TRIS: readonly { valeur: AdminReviewSort; label: string }[] = [
  { valeur: 'recent', label: 'Plus récents' },
  { valeur: 'oldest', label: 'Plus anciens' },
  { valeur: 'worst', label: 'Moins bien notés' },
  { valeur: 'best', label: 'Mieux notés' },
];

const VUE_CLE = 'fidalia:admin-reviews-view';

/** La vue mémorisée, la grille par défaut. */
function lireVue(): ReviewsView {
  try {
    return localStorage.getItem(VUE_CLE) === 'table' ? 'table' : 'grid';
  } catch {
    return 'grid';
  }
}

function AvisPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [cabinetId, setCabinetId] = useState(Route.useSearch().cabinetId ?? '');
  const [sort, setSort] = useState<AdminReviewSort>('recent');
  const [vue, setVueEtat] = useState<ReviewsView>(lireVue);
  const [ouvert, setOuvert] = useState<AdminReview | null>(null);

  const setVue = (suivante: ReviewsView) => {
    setVueEtat(suivante);
    try {
      localStorage.setItem(VUE_CLE, suivante);
    } catch {
      // Stockage bloqué (navigation privée) : le choix n'est simplement pas mémorisé.
    }
  };

  const { data, error } = useAdminReviews({
    page,
    limit: PAGE_SIZE,
    cabinetId,
    sort,
    search: debouncedSearch,
  });

  const vide = debouncedSearch
    ? `Aucun résultat pour « ${debouncedSearch} ».`
    : 'Aucun avis pour le moment.';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdRateReview />}
        title="Avis patients"
        description="Ce que les patients disent de leur cabinet, du plus récent au plus ancien."
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          type="search"
          aria-label="Rechercher un avis"
          placeholder="Cabinet, patient, commentaire…"
          icon={<MdSearch className="size-6" />}
          containerClassName="h-11 w-full sm:min-w-64 sm:flex-1"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />

        <Select
          aria-label="Filtrer par cabinet"
          icon={<MdBusiness className="size-5" />}
          containerClassName="h-11 w-full sm:w-72"
          value={cabinetId}
          onChange={(event) => {
            setCabinetId(event.target.value);
            setPage(1);
          }}
        >
          <option value="">Tous les cabinets</option>
          {data?.cabinets.map((cabinet) => (
            <option key={cabinet.id} value={cabinet.id}>
              {cabinet.nom} ({cabinet.count})
            </option>
          ))}
        </Select>

        <Select
          aria-label="Ordre d'affichage"
          icon={<MdSort className="size-5" />}
          containerClassName="h-11 w-full sm:w-56"
          value={sort}
          onChange={(event) => {
            setSort(event.target.value as AdminReviewSort);
            setPage(1);
          }}
        >
          {TRIS.map((tri) => (
            <option key={tri.valeur} value={tri.valeur}>
              {tri.label}
            </option>
          ))}
        </Select>

        <div className="flex items-center gap-4 sm:ml-auto">
          {data ? (
            <p className="flex items-center gap-1.5 text-sm text-content-muted">
              <span className="font-semibold text-content">{data.total}</span> avis
              {data.average !== null ? (
                <>
                  <span aria-hidden="true">·</span>
                  <MdStar className="size-4 text-gold-500" aria-hidden="true" />
                  <span className="font-semibold text-content tabular-nums">
                    {data.average.toFixed(1).replace('.', ',')}
                  </span>
                  <span>de moyenne</span>
                </>
              ) : null}
            </p>
          ) : null}
          <ViewSwitch value={vue} onChange={setVue} />
        </div>
      </div>

      {vue === 'grid' ? (
        !data ? (
          <AvisGrilleSqueletes />
        ) : data.items.length === 0 ? (
          <p className="py-12 text-center text-content-muted">{vide}</p>
        ) : (
          <AvisGrille avis={data.items} ouvrir={setOuvert} />
        )
      ) : (
        <DataTable
          mobileCards
          data={data?.items ?? []}
          columns={colonnesAvis(setOuvert)}
          isLoading={!data}
          skeletonRows={10}
          rowId={(avis) => avis.id}
          emptyMessage={vide}
        />
      )}

      <AvisModale avis={ouvert} fermer={() => setOuvert(null)} />

      {data ? (
        <Pagination
          page={page}
          total={data.total}
          limit={PAGE_SIZE}
          onChange={setPage}
          label="avis"
        />
      ) : null}
    </div>
  );
}
